import React, { useState, useEffect } from 'react'
import { getEventReport, getEventSummary, getEventTypes } from './EventHandlingReport.slice'
import Table from '../../../components/Table/Table'
import Badge from '../../../components/Badge'
import Panel from '../../../components/Panel'
import ComboboxAutocomplete from '../../../components/Form/ComboboxAutocomplete'
import { formatUTCDate, makeEnumReadable } from '../../../util/string'
import { EventReportEntry } from '../../../apis/types'
import { useAppDispatch, useAppSelector } from '../../../shared/redux/hooks'
import { getInputStyles } from '../../../components/Form/styles'
import Layout from '../../programs/Layout'

const EventHandlingReport: React.FC = () => {
  const dispatch = useAppDispatch()
  const [systemSource, setSystemSource] = useState<string | undefined>(undefined)
  const [eventType, setEventType] = useState<string | undefined>(undefined)
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [page, setPage] = useState(0)
  const resultsPerPage = 20

  const { events, summary, eventsLoading, eventTypes } = useAppSelector((state) => state.eventHandlingReport)

  // System source options for filter
  const systemSourceOptions = [
    { id: 'PGE_PORTAL', name: 'PG&E Portal' },
    { id: 'PGE_SHARE_MY_DATA', name: 'PG&E Share My Data' },
    { id: 'PGE_BILL', name: 'PG&E Bill' },
    { id: 'PGE_CUSTOMER_SERVICE', name: 'PG&E Customer Service' },
    { id: 'SCE_PORTAL', name: 'SCE Portal' },
    { id: 'SCE_SHARE_MY_DATA', name: 'SCE Share My Data' },
    { id: 'SCE_BILL', name: 'SCE Bill' },
    { id: 'SCE_CUSTOMER_SERVICE', name: 'SCE Customer Service' },
    { id: 'SDGE_PORTAL', name: 'SDG&E Portal' },
    { id: 'SDGE_SHARE_MY_DATA', name: 'SDG&E Share My Data' },
    { id: 'SDGE_BILL', name: 'SDG&E Bill' },
    { id: 'SDGE_CUSTOMER_SERVICE', name: 'SDG&E Customer Service' },
    { id: 'INTERNAL_SYSTEM', name: 'Internal System' },
    { id: 'CUSTOMER_INPUT', name: 'Customer Input' },
    { id: 'THIRD_PARTY_API', name: 'Third Party API' },
  ]

  // Event type options (fetched from backend)
  const eventTypeOptions = eventTypes.map((type) => ({ id: type, name: type }))

  // Reset page to 0 when filters change
  useEffect(() => {
    setPage(0)
  }, [systemSource, eventType, startDate, endDate])

  // Load data when filters change
  useEffect(() => {
    const query = {
      systemSource,
      eventType,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      processorFilter: 'AutomatedEventProcessor', // Filter for automated events only
      page,
      resultsPerPage,
    }
    void dispatch(getEventReport(query))
    void dispatch(getEventSummary(query))
  }, [systemSource, eventType, startDate, endDate, page, dispatch])

  // Load event types when filter context changes (but not page or eventType selection)
  useEffect(() => {
    const query = {
      systemSource,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      processorFilter: 'AutomatedEventProcessor',
    }
    void dispatch(getEventTypes(query))
  }, [systemSource, startDate, endDate, dispatch])

  const handlePaginatorClick = (newPage: number) => {
    setPage(newPage)
  }

  return (
    <Layout title="Event Handling Report">
      <div>
        {/* Summary Statistics */}
        {summary && (
          <Panel header="Automated Event Statistics" className="mb-5">
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900 dark:text-slate-100">{summary.automatedEvents}</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Automated Events</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400">{summary.successfulEvents}</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Successful</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 dark:text-red-400">{summary.failedEvents}</div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Failed</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {summary.successRate != null ? summary.successRate.toFixed(1) : '0.0'}%
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">Success Rate</div>
              </div>
            </div>
            {Object.keys(summary.eventTypeBreakdown).length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Event Types</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(summary.eventTypeBreakdown).map(([type, count]) => (
                    <span
                      key={type}
                      className="inline-flex items-center px-2 py-1 ring-1 ring-inset rounded-full text-xs font-medium bg-gray-50 dark:bg-gray-400/10 text-gray-800 dark:text-gray-400 ring-gray-600/10 dark:ring-gray-400/20"
                    >
                      {makeEnumReadable(type)}: {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Panel>
        )}

        {/* Filters */}
        <Panel header="Filters" className="mb-5">
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Start Date</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={getInputStyles()} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">End Date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={getInputStyles()} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">System Source</label>
              <ComboboxAutocomplete labelPlural="System Sources" value={systemSource} handleChange={setSystemSource} data={systemSourceOptions} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Event Type</label>
              <ComboboxAutocomplete labelPlural="Event Types" value={eventType} handleChange={setEventType} data={eventTypeOptions} />
            </div>
          </div>
        </Panel>

        {/* Events Table */}
        <Table<EventReportEntry>
          colConfig={[
            {
              label: 'Transaction Date',
              render: (item) => formatUTCDate(new Date(item.transactionDate)),
              isSortable: false,
            },
            {
              label: 'Event Type',
              render: (item) => (
                <div>
                  <div className="font-medium">{makeEnumReadable(item.eventType)}</div>
                  {item.eventSubType && <div className="text-xs text-slate-500">{makeEnumReadable(item.eventSubType)}</div>}
                </div>
              ),
              isSortable: false,
            },
            {
              label: 'System Source',
              render: (item) => makeEnumReadable(item.systemSource),
              isSortable: false,
            },
            {
              label: 'Property',
              render: (item) => item.propertyName || <span className="text-slate-400">N/A</span>,
              isSortable: false,
            },
            {
              label: 'Tenant',
              render: (item) => item.tenantName || <span className="text-slate-400">N/A</span>,
              isSortable: false,
            },
            {
              label: 'Processor',
              render: (item) => (
                <div className="text-xs">
                  {item.processingRecords.length > 0 ? (
                    <div>
                      <div>{item.processingRecords[0].processor}</div>
                      <div className="text-slate-500">{formatUTCDate(new Date(item.processingRecords[0].processedAt))}</div>
                    </div>
                  ) : (
                    <span className="text-slate-400">Not processed</span>
                  )}
                </div>
              ),
              isSortable: false,
            },
            {
              label: 'Status',
              render: (item) => {
                const hasErrors = item.processingErrors.length > 0
                return (
                  <div>
                    <Badge status={hasErrors ? 'FAILED' : 'SUCCEEDED'} />
                    {hasErrors && (
                      <div className="text-xs text-red-600 dark:text-red-400 mt-1" title={item.processingErrors[0].error}>
                        {item.processingErrors[0].error.substring(0, 50)}
                        {item.processingErrors[0].error.length > 50 ? '...' : ''}
                      </div>
                    )}
                  </div>
                )
              },
              isSortable: false,
            },
          ]}
          data={events ?? { results: [], total: 0, page: 0, resultsPerPage: 20 }}
          isLoading={eventsLoading}
          handlePaginatorClick={handlePaginatorClick}
        />
      </div>
    </Layout>
  )
}

export default EventHandlingReport
